import { useState, useEffect } from 'react';
import { Eye, EyeOff, LogIn, Lock } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 5 * 60 * 1000; // 5 dakika
const LS_FAIL_KEY = 'gmc_login_failures';

interface LoginFailures { count: number; lockedUntil: number | null; }

function getFailures(): LoginFailures {
  try { return JSON.parse(localStorage.getItem(LS_FAIL_KEY) || '{}'); } catch { return { count: 0, lockedUntil: null }; }
}
function saveFailures(f: LoginFailures) { localStorage.setItem(LS_FAIL_KEY, JSON.stringify(f)); }

export default function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lockMins, setLockMins] = useState(0);

  const isLocked = lockMins > 0;

  useEffect(() => {
    const tick = () => {
      const { lockedUntil } = getFailures();
      if (!lockedUntil) { setLockMins(0); return; }
      const remaining = Math.ceil((lockedUntil - Date.now()) / 60000);
      if (remaining <= 0) { saveFailures({ count: 0, lockedUntil: null }); setLockMins(0); }
      else { setLockMins(remaining); }
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('E-posta ve şifre zorunludur.'); return; }
    if (isLocked) return;
    setLoading(true);
    setError(null);
    const { error: err } = await login(email, password, rememberMe);
    setLoading(false);
    if (err) {
      const f = getFailures();
      const newCount = (f.count || 0) + 1;
      if (newCount >= MAX_ATTEMPTS) {
        saveFailures({ count: newCount, lockedUntil: Date.now() + LOCK_DURATION_MS });
        setLockMins(Math.ceil(LOCK_DURATION_MS / 60000));
      } else {
        saveFailures({ count: newCount, lockedUntil: null });
        setError(err);
      }
    } else {
      saveFailures({ count: 0, lockedUntil: null });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <div className="fixed -top-[30%] -left-[15%] w-[60%] h-[60%] rounded-full blur-[150px] pointer-events-none" style={{ background: 'rgba(110,231,183,0.04)' }} />
      <div className="fixed -bottom-[30%] -right-[15%] w-[60%] h-[60%] rounded-full blur-[150px] pointer-events-none" style={{ background: 'rgba(196,181,253,0.04)' }} />

      <div className="w-full max-w-sm relative z-10">
        {/* Logo & Başlık */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-ok-subtle border border-ok mb-4">
            <span className="display text-2xl" style={{ color: 'var(--ok)' }}>GM</span>
          </div>
          <h1 className="display text-2xl" style={{ color: 'var(--text-primary)' }}>
            Gurme <span className="italic-display" style={{ color: 'var(--text-3)', fontSize: '0.9em' }}>Komuta</span>
          </h1>
          <p className="text-sm text-3 mt-1">Mantar Üretim Yönetim Sistemi</p>
        </div>

        {/* Form kartı */}
        <div className="card p-6 space-y-5">
          <div className="flex items-center gap-2 pb-4" style={{ borderBottom: '1px solid var(--line)' }}>
            <Lock className="w-4 h-4 text-ok" />
            <span className="text-sm font-semibold text-2">Giriş Yap</span>
          </div>

          {isLocked && (
            <div role="alert" className="flex items-start gap-2 p-3 rounded-lg bg-crit-subtle border border-crit">
              <span className="text-crit text-xs leading-snug">
                Çok fazla başarısız deneme. <span className="font-bold">{lockMins} dakika</span> bekleyin.
              </span>
            </div>
          )}

          {error && !isLocked && (
            <div id="login-error" role="alert" className="flex items-start gap-2 p-3 rounded-lg bg-crit-subtle border border-crit">
              <span className="text-crit text-xs leading-snug">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="login-email" className="eyebrow">E-posta</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="ornek@eposta.com"
                disabled={loading || isLocked}
                className="w-full px-3 py-2.5 rounded-xl text-sm input-token"
                autoComplete="email"
                aria-required="true"
                aria-describedby={error && !isLocked ? 'login-error' : undefined}
              />
            </div>

            {/* Şifre */}
            <div className="space-y-1.5">
              <label htmlFor="login-password" className="eyebrow">Şifre</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading || isLocked}
                  className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm input-token"
                  autoComplete="current-password"
                  aria-required="true"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors cursor-pointer"
                  style={{ color: 'var(--text-3)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-2)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-3)')}
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                </button>
              </div>
            </div>

            {/* Beni hatırla */}
            <label htmlFor="login-remember" className="flex items-center gap-2.5 cursor-pointer group">
              {/* Visually hidden native checkbox — keyboard & screen-reader accessible */}
              <input
                id="login-remember"
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                className="sr-only"
              />
              {/* Custom visual indicator driven by the native checkbox state */}
              <span
                aria-hidden="true"
                className="w-4 h-4 rounded flex items-center justify-center transition-all flex-shrink-0 border"
                style={rememberMe
                  ? { background: 'var(--ok)', borderColor: 'var(--ok)' }
                  : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.2)' }
                }
              >
                {rememberMe && (
                  <svg className="w-2.5 h-2.5" viewBox="0 0 10 8" fill="none" style={{ color: 'var(--ink-0)' }}>
                    <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-xs text-3 select-none">Beni hatırla (30 gün)</span>
            </label>

            {/* Giriş butonu */}
            <button
              type="submit"
              disabled={loading || isLocked}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98]"
              style={{ background: 'var(--ok-bg)', borderColor: 'rgba(110,231,183,0.2)', color: 'var(--ok)' }}
              onMouseEnter={e => { if (!loading && !isLocked) e.currentTarget.style.background = 'rgba(110,231,183,0.15)'; }}
              onMouseLeave={e => { if (!loading && !isLocked) e.currentTarget.style.background = 'var(--ok-bg)'; }}
            >
              {loading ? (
                <span className="w-4 h-4 border-2 spin-ok rounded-full animate-spin" />
              ) : (
                <LogIn className="w-4 h-4" />
              )}
              {loading ? 'Giriş yapılıyor…' : 'Giriş Yap'}
            </button>
          </form>
        </div>

        <p className="text-center mono text-[11px] mt-6" style={{ color: 'var(--text-4)' }}>
          Erişim bilgileriniz için sistem yöneticisiyle iletişime geçin.
        </p>
      </div>
    </div>
  );
}
